import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router";
import axios from 'axios';

import { AppContext } from '../../context.js';
import { FilePlus, Search } from "../../files/icons/index.js";
import { DateToFormat } from "../../utils/datetime.js";

import PatientCard from '../components/patientCard.js';
import Loading from '../components/loading.js';
import Helmet from "../components/helmet.js";

const STATUS_TABS = [
  { key: "active",    label: "Active",    statuses: ["ACTIVE"] },
  { key: "draft",     label: "Draft",     statuses: ["DRAFT"] },
  { key: "archived",  label: "Archived",  statuses: ["ARCHIVED"] },
  // Unaligning a patient sets statusType=CANCELED and lifecycleStage=UNALIGNED,
  // so this tab pins the lifecycle stage to surface them on their own.
  { key: "unaligned", label: "Unaligned", statuses: ["CANCELED"], stages: ["UNALIGNED"] },
];

const LIFECYCLE_TABS = [
  { key: "all",  label: "All",            stages: [] },
  { key: "pre",  label: "Pre-Alignment",  stages: ["PRE_ALIGNMENT"] },
  { key: "post", label: "Post-Alignment", stages: ["POST_ALIGNMENT_NEW", "POST_ALIGNMENT_EST"] },
];

function Patients(props) {
  const context = useContext(AppContext);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const step = 10;
  const [prompt, setPrompt] = useState("");
  const [statusTab, setStatusTab] = useState("active");
  const [lifecycleTab, setLifecycleTab] = useState("all");
  const [limit, setLimit] = useState(step);
  const [count, setCount] = useState(0);

  const [patients, setPatients] = useState([]);
  const [activeProjects, setActiveProjects] = useState({}); // patientID -> active project

  function statusesForTab(tab) {
    return STATUS_TABS.find(t => t.key === tab)?.statuses || ["ACTIVE"];
  }

  function lifecycleStagesForTab(tab) {
    return LIFECYCLE_TABS.find(t => t.key === tab)?.stages || [];
  }

  function stagesForStatusTab(tab) {
    return STATUS_TABS.find(t => t.key === tab)?.stages || [];
  }

  useEffect(() => {
    load(statusTab, lifecycleTab, "", step, 0, true);
  }, [statusTab, lifecycleTab]);

  // Load each visible patient's active project so the card can label its stage
  // badge with the project title and show the monthly check-in badge.
  useEffect(() => {
    if (patients.length === 0) {
      setActiveProjects({});
      return;
    }
    // Current billing month (client-local "YYYY-MM"), matching the value stored on check-in.
    const month = DateToFormat(new Date(), "YYYY-MM");
    axios.post('/api/admin/projects', {
      filter: { patientID: patients.map((p) => p.id), archived: false },
      month: month,
    }).then((response) => {
      const map = {};
      (response.data.projects || []).forEach((project) => {
        if (project.statusType === "COMPLETED" || project.statusType === "CANCELED") return;
        if (!map[project.patientID]) map[project.patientID] = project; // first active project wins
      });
      setActiveProjects(map);
    }).catch((e) => {
      context.addError(e);
    });
  }, [patients]);

  function load(tab, lifecycle, searchPrompt, lim, offset, reset) {
    let u = context.addSpiner();
    axios.post('/api/admin/patients', {
      filter: {
        status: statusesForTab(tab),
        lifecycleStage: tab === "active" ? lifecycleStagesForTab(lifecycle) : stagesForStatusTab(tab),
        patientName: searchPrompt ? searchPrompt.trim().split(/\s+/) : [],
      },
      limit: lim,
      offset: offset,
      includeMissingRequired: true,
    }).then((response) => {
      if (reset) {
        setPatients(response.data.patients);
      } else {
        setPatients((prev) => {
          const incoming = response.data.patients.filter(
            (n) => !prev.some((p) => p.id === n.id)
          );
          return [...prev, ...incoming];
        });
      }
      setLimit(response.data.limit);
      setCount(response.data.count);
      setLoading(false);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function switchTab(tab) {
    setStatusTab(tab);
    setLifecycleTab("all");
    setPrompt("");
    setLimit(step);
    setCount(0);
  }

  function switchLifecycleTab(tab) {
    setLifecycleTab(tab);
    setPrompt("");
    setLimit(step);
    setCount(0);
  }

  function search() {
    load(statusTab, lifecycleTab, prompt, step, 0, true);
    setLimit(step);
  }

  function extendLimit() {
    load(statusTab, lifecycleTab, prompt, limit + step, limit, false);
  }

  function renderPatients() {
    if (patients.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <div className="typo-normal-150 text-muted">No patients found</div>
        </div>
      );
    }

    return patients.map((patient) => (
      <PatientCard key={patient.id} patient={patient} link={true} activeProject={activeProjects[patient.id]} hideCheckin={true} />
    ));
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Helmet title="Patients" />

      <div className="container">
        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="d-flex justify-content-between align-items-center">
              <span className="typo-hero-small">Patients</span>
              <div className="w-25 text-end">
                <div className="item typo-normal-150 ms-2" onClick={() => navigate('/patient/create')}><FilePlus height={16} width={16} color={'grey'} /> Create Patient</div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12">

            <div className="card shadowmore mb-4">
              <div className="card-content sm">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex justify-content-start align-items-center">
                    {STATUS_TABS.map((tab) => (
                      <div
                        key={tab.key}
                        className={"item typo-normal-150 " + (statusTab === tab.key ? "active" : "") + (tab.key !== "active" ? " ms-2" : "")}
                        onClick={() => switchTab(tab.key)}
                      >
                        {tab.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {statusTab === "active" && (
                <div className="card-content sm">
                  <div className="d-flex justify-content-start align-items-center">
                    {LIFECYCLE_TABS.map((tab) => (
                      <div
                        key={tab.key}
                        className={"item typo-normal-150 " + (lifecycleTab === tab.key ? "active" : "") + (tab.key !== "all" ? " ms-2" : "")}
                        onClick={() => switchLifecycleTab(tab.key)}
                      >
                        {tab.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="card-content sm">
                <div className="d-flex justify-content-start align-items-center w-100">
                  <Search height={16} width={16} color={'grey'} />
                  <input
                    type="text"
                    className="input borderless w-100 ms-2"
                    placeholder="Type and press enter to search..."
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyUp={(e) => e.key === 'Enter' && search()}
                    autoFocus={true}
                  />
                </div>
              </div>
            </div>

            {renderPatients()}

            {count !== 0 && limit < count && (
              <div className="d-flex justify-content-center align-items-center">
                <div className="item typo-normal-150" onClick={extendLimit}>show more</div>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}

export default Patients;
